/**
 * Derived views over application state.
 *
 * Components must never recompute shifts, eligibility or display names
 * themselves - they ask here. Each selector memoises on the identity of its
 * inputs, which is safe because the store only ever replaces objects, never
 * mutates them.
 */

import { DAY_KEYS } from '../core/time.js';
import {
  buildWeekShifts,
  displayNameFor,
  groupBySlot,
  indexStaffRules,
  normaliseRota,
} from '../core/model.js';
import { resolveDayConfig } from '../core/settings.js';
import { assessDay } from '../scheduler/eligibility.js';

/** Memoise a unary function on reference equality of its argument tuple. */
function memoise(fn, keyCount = 1) {
  let lastKeys = null;
  let lastValue;
  return (...args) => {
    const keys = args.slice(0, keyCount);
    if (lastKeys && keys.every((key, index) => key === lastKeys[index])) return lastValue;
    lastKeys = keys;
    lastValue = fn(...args);
    return lastValue;
  };
}

/** The rota for the currently selected week, or null. */
export function currentRota(state) {
  return state.rotas?.[state.weekCommencing] ?? null;
}

/** The schedule for the currently selected week, or null. */
export function currentSchedule(state) {
  return state.schedules?.[state.weekCommencing] ?? null;
}

/** True when there is nothing to schedule yet. */
export function hasRota(state) {
  const rota = currentRota(state);
  return Boolean(rota?.rows?.some((row) => row.name?.trim()));
}

const normalisedRotaMemo = memoise((rota) => (rota ? normaliseRota(rota) : null));

/** The current rota with employee ids and duplicate flags resolved. */
export function normalisedRota(state) {
  return normalisedRotaMemo(currentRota(state));
}

const weekShiftsMemo = memoise((rota, settings) => (rota ? buildWeekShifts(rota, settings) : null), 2);

/** ShiftRecords for the whole week, keyed by day. */
export function weekShifts(state) {
  return weekShiftsMemo(currentRota(state), state.settings);
}

const rulesIndexMemo = memoise((rules) => indexStaffRules(rules ?? []));

/** Staff rules indexed by employee id. */
export function rulesIndex(state) {
  return rulesIndexMemo(state.staffRules);
}

const eligibilityMemo = memoise(
  (shifts, settings, rules) => {
    if (!shifts) return null;
    const result = {};
    for (const dayKey of DAY_KEYS) {
      result[dayKey] = assessDay(shifts[dayKey] ?? [], {
        settings,
        dayConfig: resolveDayConfig(settings, dayKey),
        rulesIndex: rules,
      });
    }
    return result;
  },
  3,
);

/** Eligibility results for every person on every day. */
export function weekEligibility(state) {
  return eligibilityMemo(weekShifts(state), state.settings, rulesIndex(state));
}

/** Eligibility results for one day. */
export function dayEligibility(state, dayKey) {
  return weekEligibility(state)?.[dayKey] ?? [];
}

/** Resolved coverage configuration for one day. */
export function dayConfigFor(state, dayKey) {
  return resolveDayConfig(state.settings, dayKey);
}

/** Assignments for one day, ascending by slot. */
export function dayAssignments(state, dayKey) {
  const assignments = currentSchedule(state)?.days?.[dayKey]?.assignments ?? [];
  return [...assignments].sort((a, b) => (a.slotMinutes ?? 0) - (b.slotMinutes ?? 0));
}

/** Assignments for one day grouped into slots. */
export function daySlots(state, dayKey) {
  return groupBySlot(dayAssignments(state, dayKey));
}

/** Every warning for the week, week-scoped first. */
export function allWarnings(state) {
  const schedule = currentSchedule(state);
  if (!schedule) return [];
  const dayWarnings = DAY_KEYS.flatMap((dayKey) => schedule.days?.[dayKey]?.warnings ?? []);
  return [...(schedule.warnings ?? []), ...dayWarnings];
}

/** Warnings that have not been acknowledged, tallied by severity. */
export function warningCounts(state) {
  const counts = { error: 0, warning: 0, info: 0, acknowledged: 0 };
  for (const warning of allWarnings(state)) {
    if (warning.acknowledged) counts.acknowledged += 1;
    else counts[warning.severity] = (counts[warning.severity] ?? 0) + 1;
  }
  return counts;
}

/** Warnings attached to one employee on one day. */
export function warningsFor(state, dayKey, employeeId) {
  return allWarnings(state).filter(
    (warning) => warning.dayKey === dayKey && warning.employeeId === employeeId,
  );
}

/** Full rota name for an employee id in the current week. */
export function fullNameFor(state, employeeId) {
  const rota = normalisedRota(state);
  return rota?.rows?.find((row) => row.employeeId === employeeId)?.name ?? '';
}

/** Short display name used in exports and chips. */
export function displayName(state, employeeId) {
  return displayNameFor(state.aliasBook, employeeId, fullNameFor(state, employeeId));
}

/** Every distinct employee in the current rota, ordered by name. */
export function employees(state) {
  const rota = normalisedRota(state);
  if (!rota) return [];
  const seen = new Set();
  return rota.rows
    .filter((row) => {
      if (!row.name?.trim() || seen.has(row.employeeId)) return false;
      seen.add(row.employeeId);
      return true;
    })
    .map((row) => ({
      employeeId: row.employeeId,
      name: row.name,
      displayName: displayNameFor(state.aliasBook, row.employeeId, row.name),
    }))
    .sort((a, b) => a.name.localeCompare(b.name, 'en-GB'));
}

/** Unresolved aliases that still need a manager decision. */
export function unresolvedAliases(state) {
  return (state.aliasBook?.unresolved ?? []).filter((entry) => !entry.resolvedTo);
}

/** Everyone eligible on a day who has no assignment yet. */
export function unassignedEligible(state, dayKey) {
  const assigned = new Set(dayAssignments(state, dayKey).map((item) => item.employeeId));
  return dayEligibility(state, dayKey).filter(
    (entry) => entry.eligible && !assigned.has(entry.employeeId),
  );
}
